/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import type { ShopperSearch } from '@/scapi';
import DefaultRefinement from '../refine-default';
import type { RefinementProps } from '../types';

/**
 * PRICE IS BANDS ONLY. NO MIN/MAX ENTRY ROW.
 *
 * `[ours-extra]`: neither brand's live rail carries a free-entry price row.
 * That is the owner's direct observation of live on both brands, and it is
 * the only source for it — the one live capture on disk with a facet group
 * expanded has BG's PRICE group COLLAPSED, so it renders no children and
 * cannot corroborate or refute anything about this row. A collapsed group is
 * not evidence of absence, and it is not being read as any.
 *
 * WHAT THE REMOVED ROW ACTUALLY DID, established before deleting it rather
 * than after: two `type="number"` inputs placeholdered Min and Max, whose
 * Apply handler wrote `toggleFilter('price', '(min..max)')` — the SAME
 * `refine=price=(a..b)` param the bands below already write. So the bands and
 * the row were never two mechanisms; they were one mechanism with two entry
 * affordances, and the row's only unique capability was choosing a bound the
 * bands do not offer, e.g. `(75..220)`.
 *
 * That capability is an ENTRY affordance, not a routing one. Nothing about
 * the URL contract changes here: an arbitrary `refine=price=(75..220)` still
 * arrives, still filters, and is still removable from the active-filter row,
 * because every one of those paths reads the param generically and none of
 * them went through the inputs. What is lost is a shopper's ability to TYPE
 * such a bound — which is precisely the thing live does not offer either.
 *
 * `PriceRangeInput` is left on disk rather than deleted with its last rail
 * consumer: `src/theme/overrides/text-field.css` still names it, and that
 * file is outside this change. Removing the component is a separate, cheap
 * decision once that reference is settled.
 */
export default function RefinePrice({
    values,
    attributeId,
    isFilterSelected,
    toggleFilter,
}: RefinementProps & {
    /**
     * Still accepted, no longer read. The rail passes `result` so this group
     * could derive min/max bounds for the removed inputs' validation; the
     * prop stays in the signature so the call site is unchanged.
     */
    result?: ShopperSearch.schemas['ProductSearchResult'];
}): ReactElement {
    return (
        <DefaultRefinement
            values={values}
            attributeId={attributeId}
            isFilterSelected={isFilterSelected}
            toggleFilter={toggleFilter}
        />
    );
}
